import api from '../services/Api';
import {
	fetchingCountriesAction,
	fetchedCountriesAction,
	fetchingRomaniaCountiesAndCitiesAction,
	fetchedRomaniaCountiesAndCitiesAction
} from '../actions/RaffleFormActions';
import { fetchingShippingTableAction, fetchedShippingTableAction } from '../actions/ShippingPageActions';
import { networkErrorAction } from '../actions/UiActions';
import CompoundFetch from '../helpers/CompoundFetch';

/**
 * @typedef {SelectOption} CountryOption
 * @property {DeliveryService[]} deliveryServices
 */

/**
 * @typedef {Object} CountryData
 * @property {string} code
 * @property {string} name
 * @property {DeliveryService[]} deliveryServices
 */

class DeliveryRepository {

	/**
	 * @param {CountryData[]} countries
	 * @return {SelectOption[]}
	 */
	countriesToSelectOptions(countries) {
		return countries.map(this.countryToSelectOption);
	}

	/**
	 * @param {CountryData} country
	 * @return {SelectOption}
	 */
	countryToSelectOption(country) {
		return {
			value: country.code,
			label: country.name,
			deliveryServices: country.deliveryServices
		};
	}

	/**
	 * @param {string[]} usStates
	 * @return {SelectOption[]}
	 */
	usStatesToSelectOptions(usStates) {
		return Object.keys(usStates).map((key) => this.stateToSelectOption(key, usStates[key]));
	}

    /**
     * @param {string[]} caStates
     * @return {SelectOption[]}
     */
    caStatesToSelectOptions(caStates) {
        return Object.keys(caStates).map((key) => this.stateToSelectOption(key, caStates[key]));
    }

	/**
	 * @param {string} code
	 * @param {string} name
	 * @return {SelectOption}
	 */
	stateToSelectOption(code, name) {
		return {
			value: code,
			label: name
		};
	}

	/**
	 * @return {function(*=): (PromiseLike<T> | Promise<T> | *)}
	 */
	fetchCountries = () => (dispatch) => {
		dispatch(fetchingCountriesAction());
		return api.post('/compound-fetch', {
			countries: '/delivery/countries',
			usStates: '/delivery/us-states',
			caStates: '/delivery/ca-states'
		}).then((result) => {
			const data = result.data;
			let countries = CompoundFetch.parseSubRequest(dispatch, data.countries, 'Countries');
			let usStates = CompoundFetch.parseSubRequest(dispatch, data.usStates, 'US States');
			let caStates = CompoundFetch.parseSubRequest(dispatch, data.caStates, 'CA States');

			if (countries && usStates && caStates) {
				dispatch(fetchedCountriesAction(
					this.countriesToSelectOptions(countries),
					this.usStatesToSelectOptions(usStates),
					this.caStatesToSelectOptions(caStates)
				));
			}
			else {
				const msg = 'Compound fetch returned invalid data for method fetchCountries.';
				dispatch(networkErrorAction(msg));
			}
		}).catch((error) => {
			dispatch(networkErrorAction(error));
		});
	};

	/**
	 * @return {function(*=): (PromiseLike<T> | Promise<T> | *)}
	 */
	fetchRomanianCountiesAndCities = () => (dispatch) => {
		dispatch(fetchingRomaniaCountiesAndCitiesAction());
		return api.get('/delivery/romania-counties-and-cities').then(result => {
			dispatch(fetchedRomaniaCountiesAndCitiesAction(result.data));
		}).catch((error) => {
			dispatch(networkErrorAction(error));
		});
	};

	/**
	 * @return {function(*=): (PromiseLike<T> | Promise<T> | *)}
	 */
	fetchShippingTable = () => (dispatch) => {
		dispatch(fetchingShippingTableAction());
		return api.get('/delivery/shipping').then(result => {
			dispatch(fetchedShippingTableAction(result.data));
		}).catch((error) => {
			dispatch(networkErrorAction(error));
		});
	};
}

export default new DeliveryRepository();



// WEBPACK FOOTER //
// ./repositories/DeliveryRepository.js